import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mergeAuthFromChildWindowUrlParam } from '../utils/googleAuth';
import DualGoogleSlidesEmbed from './DualGoogleSlidesEmbed';
import { advancePlaylistToNextDeckIfAtLastSlide } from '../utils/playlistAdvanceFromLocalStorage';
import {
  AUDIENCE_AUDIO_FOCUS_KEY,
  writeAudienceAudioFocusToStorage,
} from '../utils/audienceAudioFocus';
import { broadcastCurrentSlideState, DECKPACK_CURRENT_SLIDE_EVENT } from '../utils/currentSlideBroadcast';
import '../styles/PresentationView.css';

function PresentationView() {
  const [searchParams] = useSearchParams();
  const initialPresentationId = searchParams.get('presentationId');
  const authParam = searchParams.get('auth');
  const slideIndexParam = searchParams.get('slideIndex');
  const [currentSlideIndex, setCurrentSlideIndex] = useState(
    slideIndexParam ? parseInt(slideIndexParam) : 0
  );
  const [activePresentationId, setActivePresentationId] = useState(initialPresentationId);
  /** From `current-slide-state.slideCount` — used for playlist auto-advance on the last slide. */
  const [deckSlideCount, setDeckSlideCount] = useState(0);
  // Add overlay state
  const [overlay, setOverlay] = useState('none');
  /** Only the focused Audience window should play deck audio; background audience tabs stay muted. */
  const [audienceAudioUnmuted, setAudienceAudioUnmuted] = useState(() => {
    try {
      return !document.hidden && document.hasFocus();
    } catch {
      return true;
    }
  });
  const audienceSlidesEmbedRef = useRef(null);
  const currentSlideIndexRef = useRef(currentSlideIndex);
  currentSlideIndexRef.current = currentSlideIndex;
  const activePresentationIdRef = useRef(activePresentationId);
  activePresentationIdRef.current = activePresentationId;
  const deckSlideCountRef = useRef(deckSlideCount);
  deckSlideCountRef.current = deckSlideCount;

  const audienceSlidesSrc = useMemo(() => {
    if (!activePresentationId) return '';
    return `https://docs.google.com/presentation/d/${activePresentationId}/preview?rm=minimal&delayms=0&loop=false&speed=1&slide=${currentSlideIndex + 1}&nav=0&start=false`;
  }, [activePresentationId, currentSlideIndex]);

  // Merge URL auth into localStorage without dropping refresh_token from the parent tab
  useEffect(() => {
    if (authParam) mergeAuthFromChildWindowUrlParam(authParam);
  }, [authParam]);

  // Audience View is the single audio sink for deck media when this window is open.
  useEffect(() => {
    const key = 'audience-view-open';
    try {
      localStorage.setItem(key, '1');
    } catch {
      /* ignore */
    }
    const clear = () => {
      try {
        localStorage.removeItem(key);
        localStorage.removeItem(AUDIENCE_AUDIO_FOCUS_KEY);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('beforeunload', clear);
    window.addEventListener('pagehide', clear);
    return () => {
      clear();
      window.removeEventListener('beforeunload', clear);
      window.removeEventListener('pagehide', clear);
    };
  }, []);

  // Tell the console (and other tabs) whether this audience window should own deck audio.
  useEffect(() => {
    const sync = () => {
      const on = !document.hidden && document.hasFocus();
      setAudienceAudioUnmuted(on);
      writeAudienceAudioFocusToStorage(on);
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', sync);
    window.addEventListener('blur', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', sync);
      window.removeEventListener('blur', sync);
      writeAudienceAudioFocusToStorage(false);
    };
  }, []);

  // Watch for slide changes from localStorage (control panel is the source of truth)
  useEffect(() => {
    let lastObservedIndex = -1;
    let lastObservedPresentationId = null;
    let lastObservedSlideCount = -1;

    const applyState = (state) => {
      if (!state || !state.presentationId) return;
      const index = typeof state.index === 'number' ? state.index : 0;
      const slideCount = typeof state.slideCount === 'number' ? state.slideCount : 0;
      const presentationChanged = state.presentationId !== lastObservedPresentationId;
      const indexChanged = index !== lastObservedIndex;
      const slideCountChanged = slideCount !== lastObservedSlideCount;
      if (!presentationChanged && !indexChanged && !slideCountChanged) return;

      lastObservedSlideCount = slideCount;
      if (presentationChanged || indexChanged) {
        lastObservedIndex = index;
        lastObservedPresentationId = state.presentationId;
        setCurrentSlideIndex(index);
        setActivePresentationId(state.presentationId);
      }
      setDeckSlideCount(slideCount);
    };

    const checkForUpdates = () => {
      const stateStr = localStorage.getItem('current-slide-state');
      if (!stateStr) return;
      try {
        const state = JSON.parse(stateStr);
        applyState(state);
      } catch (error) {
        console.error("Error parsing state:", error);
      }
    };

    // Initial sync
    checkForUpdates();

    // Listen for cross-window storage events
    const handleStorageChange = (e) => {
      if (e.key === 'current-slide-state' && e.newValue) {
        try {
          const state = JSON.parse(e.newValue);
          applyState(state);
        } catch (error) {
          console.error("Error parsing storage event state:", error);
        }
      }
    };

    const onSameTabBroadcast = (ev) => {
      if (ev?.detail) applyState(ev.detail);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(DECKPACK_CURRENT_SLIDE_EVENT, onSameTabBroadcast);

    // Also poll as a safety net (same-tab writes do not fire `storage`)
    const interval = setInterval(checkForUpdates, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(DECKPACK_CURRENT_SLIDE_EVENT, onSameTabBroadcast);
      clearInterval(interval);
    };
  }, []);

  // Console / remote “Reveal” — mirror in-slide advance on the audience embed.
  useEffect(() => {
    const onReveal = (e) => {
      if (e.key !== 'deckpack-embed-reveal' || !e.newValue) return;
      try {
        audienceSlidesEmbedRef.current?.nudgeInSlideAdvance?.();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', onReveal);
    return () => window.removeEventListener('storage', onReveal);
  }, []);

  useEffect(() => {
    const onPointerDownCapture = (e) => {
      const t = e.target;
      if (!t || typeof t.closest !== 'function') return;
      if (t.closest('iframe.slides-embed-pane')) return;
      if (t.tagName === 'IFRAME' && t.classList?.contains('slides-embed-pane')) return;
      try {
        audienceSlidesEmbedRef.current?.blurEmbeds?.();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('pointerdown', onPointerDownCapture, true);
    return () => window.removeEventListener('pointerdown', onPointerDownCapture, true);
  }, []);

  // Forward keys: cross-tab bump + advance stored slide index (keeps audience/console/presenter aligned).
  useEffect(() => {
    const shouldDeferDeckKeys = (el) =>
      el &&
      typeof el.closest === 'function' &&
      el.closest('input, textarea, select, [contenteditable="true"]');

    const bumpAll = () => {
      try {
        audienceSlidesEmbedRef.current?.nudgeInSlideAdvance?.();
      } catch {
        /* ignore */
      }
      try {
        localStorage.setItem('deckpack-embed-reveal', String(Date.now()));
      } catch {
        /* ignore */
      }
    };

    const readAutoAdvance = () => {
      try {
        const raw = localStorage.getItem('deckpack-auto-advance');
        if (raw == null) return false;
        return JSON.parse(raw) === true;
      } catch {
        return false;
      }
    };

    const persistAndSetIndex = (idx) => {
      const pid = activePresentationIdRef.current;
      if (!pid) return;
      const total = deckSlideCountRef.current ?? 0;
      const clamped = total > 0 ? Math.max(0, Math.min(idx, total - 1)) : Math.max(0, idx);
      const payload = {
        presentationId: pid,
        index: clamped,
        slideCount: total,
        timestamp: Date.now(),
      };
      try {
        localStorage.setItem('current-slide-state', JSON.stringify(payload));
        broadcastCurrentSlideState(payload);
      } catch {
        /* ignore */
      }
      setCurrentSlideIndex(clamped);
      queueMicrotask(() => {
        try {
          audienceSlidesEmbedRef.current?.blurEmbeds?.();
        } catch {
          /* ignore */
        }
      });
    };

    const goNextSlide = () => {
      const cur = currentSlideIndexRef.current;
      const total = deckSlideCountRef.current ?? 0;
      if (readAutoAdvance() && total > 0 && cur >= total - 1) {
        const next = advancePlaylistToNextDeckIfAtLastSlide({
          currentPresentationId: activePresentationIdRef.current,
          currentSlideIndex: cur,
          deckSlideCount: total,
          autoAdvance: true,
        });
        if (next?.id) {
          setActivePresentationId(next.id);
          setCurrentSlideIndex(0);
          setDeckSlideCount(0);
          try {
            const deckPayload = {
              presentationId: next.id,
              index: 0,
              slideCount: 0,
              timestamp: Date.now(),
            };
            localStorage.setItem('current-slide-state', JSON.stringify(deckPayload));
            broadcastCurrentSlideState(deckPayload);
          } catch {
            /* ignore */
          }
          queueMicrotask(() => {
            try {
              audienceSlidesEmbedRef.current?.blurEmbeds?.();
            } catch {
              /* ignore */
            }
          });
          return;
        }
      }
      persistAndSetIndex(cur + 1);
    };

    const goForwardSynced = () => {
      bumpAll();
      goNextSlide();
    };

    const onKeyDown = (e) => {
      if (!activePresentationIdRef.current) return;
      if (shouldDeferDeckKeys(e.target)) return;

      const deckForward =
        e.code === 'Space' ||
        e.key === ' ' ||
        e.code === 'PageDown' ||
        e.code === 'Period' ||
        (e.key === '.' && !e.ctrlKey && !e.metaKey && !e.altKey) ||
        e.code === 'ArrowRight' ||
        e.code === 'ArrowDown' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowDown';

      const deckBack =
        e.code === 'PageUp' ||
        e.code === 'ArrowLeft' ||
        e.code === 'ArrowUp' ||
        e.key === 'PageUp' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowUp';

      if (deckForward) {
        e.preventDefault();
        goForwardSynced();
        return;
      }

      if (deckBack) {
        e.preventDefault();
        persistAndSetIndex(currentSlideIndexRef.current - 1);
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [activePresentationId]);

  // Add listener for overlay changes
  useEffect(() => {
    const checkForOverlayUpdates = () => {
      const overlayStr = localStorage.getItem('presentation-overlay');
      if (overlayStr) {
        try {
          const overlayData = JSON.parse(overlayStr);
          setOverlay(overlayData.type);
        } catch (error) {
          console.error("Error parsing overlay state:", error);
        }
      }
    };
    
    // Check immediately on mount
    checkForOverlayUpdates();
    
    // Add event listener for storage events
    const handleStorageChange = (e) => {
      if (e.key === 'presentation-overlay') {
        checkForOverlayUpdates();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also poll as a backup
    const interval = setInterval(checkForOverlayUpdates, 150);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Go full screen when component mounts
  useEffect(() => {
    document.title = "Deck Pack - Audience View";
    const goFullScreen = async () => {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (error) {
        console.error("Couldn't enter fullscreen mode:", error);
      }
    };
    
    goFullScreen();
    
    // Exit fullscreen when component unmounts
    return () => {
      if (document.exitFullscreen && document.fullscreenElement) {
        document.exitFullscreen();
      }
    };
  }, []);

  return (
    <div className="presentation-view">
      {/* Add overlay div */}
      {overlay !== 'none' && (
        <div 
          className={`presentation-overlay ${overlay}`}
          onClick={() => setOverlay('none')}
        />
      )}
      
      <div className="preview-container">
        {/* Stack above iframe; .click-blocker blocks pointer access to Google embed. */}
        <div className="click-blocker" aria-hidden="true" />
        {activePresentationId ? (
            <DualGoogleSlidesEmbed
              ref={audienceSlidesEmbedRef}
              key={activePresentationId}
              src={audienceSlidesSrc}
              title="Google Slides presentation preview"
              allowFullScreen
              embedMuted={!audienceAudioUnmuted}
            />
        ) : null}
      </div>
    </div>
  );
}

export default PresentationView;
